import { useState, useEffect, useCallback } from 'react'
import AdminOffplanPanel from '../components/AdminOffplanPanel'
import AdminBrochurePanel from '../components/AdminBrochurePanel'
import AdminTeamPanel from '../components/AdminTeamPanel'
import AdminBuyPanel from '../components/AdminBuyPanel'
import AdminContactPanel from '../components/AdminContactPanel'
import AdminTrustPanel from '../components/AdminTrustPanel'
import AdminSettingsPanel from '../components/AdminSettingsPanel'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"
const ADMIN_TOKEN_KEY = 'victory_admin_token'

/* ── Utils ─────────────────────────────────────────────────────────────────── */
function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function fmt(iso) {
  return new Date(iso).toLocaleDateString('en-AE', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

/* ── Status Badge ──────────────────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const map = {
    pending:  { bg: 'rgba(245,158,11,0.12)', color: '#d97706', dot: '#f59e0b', label: 'Pending' },
    approved: { bg: 'rgba(16,185,129,0.12)', color: '#059669', dot: '#10b981', label: 'Approved' },
    rejected: { bg: 'rgba(239,68,68,0.12)',  color: '#dc2626', dot: '#ef4444', label: 'Rejected' },
  }
  const s = map[status] || map.pending
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      background: s.bg, color: s.color, border: `1px solid ${s.dot}30`,
      fontFamily: FONT, fontSize: '11px', fontWeight: 700,
      padding: '3px 10px', borderRadius: '50px', letterSpacing: '0.3px',
    }}>
      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: s.dot, display: 'inline-block', flexShrink: 0 }} />
      {s.label}
    </span>
  )
}

/* ── Login Page ────────────────────────────────────────────────────────────── */
function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPass, setShowPass] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Invalid credentials')
      localStorage.setItem(ADMIN_TOKEN_KEY, data.token)
      onLogin(data.token)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', fontFamily: FONT,
      background: `linear-gradient(135deg, ${NAVY} 0%, #001020 60%, #001a33 100%)`,
      display: 'flex', flexDirection: 'column',
      position: 'relative', overflow: 'hidden',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        .adm-login-input {
          width: 100%; padding: 14px 18px;
          background: rgba(255,255,255,0.06);
          border: 1.5px solid rgba(255,255,255,0.12);
          border-radius: 12px;
          color: #fff; font-family: ${FONT}; font-size: 14px;
          transition: all 0.2s; outline: none;
        }
        .adm-login-input:focus {
          border-color: ${GOLD};
          background: rgba(255,255,255,0.09);
          box-shadow: 0 0 0 3px ${GOLD}22;
        }
        .adm-login-input::placeholder { color: rgba(255,255,255,0.28); }

        @keyframes floatOrb1 {
          0%, 100% { transform: translate(0,0) scale(1); }
          50% { transform: translate(40px, -30px) scale(1.08); }
        }
        @keyframes floatOrb2 {
          0%, 100% { transform: translate(0,0) scale(1); }
          50% { transform: translate(-30px, 40px) scale(1.05); }
        }
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
      `}</style>

      {/* Decorative background orbs */}
      <div style={{ position: 'absolute', top: '-160px', right: '-160px', width: '580px', height: '580px', borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}18 0%, transparent 70%)`, animation: 'floatOrb1 12s ease-in-out infinite', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '-120px', left: '-120px', width: '460px', height: '460px', borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}10 0%, transparent 70%)`, animation: 'floatOrb2 16s ease-in-out infinite', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: '40%', left: '15%', width: '2px', height: '120px', background: `linear-gradient(to bottom, transparent, ${GOLD}30, transparent)`, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: '20%', right: '18%', width: '2px', height: '80px', background: `linear-gradient(to bottom, transparent, ${GOLD}20, transparent)`, pointerEvents: 'none' }} />

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', position: 'relative', zIndex: 1 }}>
        <div style={{ width: '100%', maxWidth: '460px', animation: 'cardIn 0.5s ease' }}>

          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <img
              src="/assets/Victory Homes logo design.png"
              alt="Victory Homes"
              style={{ height: '72px', objectFit: 'contain', filter: 'brightness(0) invert(1)', marginBottom: '16px' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
              <div style={{ height: '1px', width: '48px', background: `linear-gradient(to right, transparent, ${GOLD}60)` }} />
              <span style={{ fontFamily: FONT, fontSize: '11px', fontWeight: 700, color: GOLD, letterSpacing: '3px', textTransform: 'uppercase' }}>Admin Portal</span>
              <div style={{ height: '1px', width: '48px', background: `linear-gradient(to left, transparent, ${GOLD}60)` }} />
            </div>
          </div>

          {/* Card */}
          <div style={{
            background: 'rgba(255,255,255,0.04)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(197,160,89,0.18)',
            borderRadius: '24px',
            padding: 'clamp(28px,5vw,48px)',
            boxShadow: '0 40px 100px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)',
          }}>
            <h1 style={{ fontFamily: FONT, fontSize: '22px', fontWeight: 700, color: '#fff', marginBottom: '6px' }}>
              Welcome back
            </h1>
            <p style={{ fontFamily: FONT, fontSize: '14px', color: 'rgba(255,255,255,0.42)', marginBottom: '32px', lineHeight: 1.5 }}>
              Sign in to manage your property listings
            </p>

            <form onSubmit={handleSubmit} noValidate>
              {/* Email */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Email Address
                </label>
                <input
                  className="adm-login-input"
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError('') }}
                  placeholder="mainadmin@gmail.com"
                  required
                  autoComplete="email"
                />
              </div>

              {/* Password */}
              <div style={{ marginBottom: '28px' }}>
                <label style={{ display: 'block', fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="adm-login-input"
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={e => { setPassword(e.target.value); setError('') }}
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                    style={{ paddingRight: '50px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(p => !p)}
                    style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', fontSize: '16px', padding: '4px', lineHeight: 1, transition: 'color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.color = GOLD}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'}
                    aria-label={showPass ? 'Hide password' : 'Show password'}
                  >
                    {showPass ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px' }}>
                  <span style={{ fontSize: '15px', flexShrink: 0 }}>⚠️</span>
                  <p style={{ fontFamily: FONT, fontSize: '13px', color: '#fca5a5', margin: 0 }}>{error}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%', padding: '15px 24px',
                  background: loading ? 'rgba(197,160,89,0.35)' : `linear-gradient(135deg, ${GOLD} 0%, #a07830 100%)`,
                  border: 'none', borderRadius: '12px',
                  color: '#fff', fontFamily: FONT, fontSize: '15px', fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: loading ? 'none' : `0 8px 28px ${GOLD}45`,
                  transition: 'all 0.25s', letterSpacing: '0.3px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                }}
                onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 14px 36px ${GOLD}55` } }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = loading ? 'none' : `0 8px 28px ${GOLD}45` }}
              >
                {loading ? (
                  <>
                    <span style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                    Signing in…
                  </>
                ) : (
                  <>Sign In to Dashboard</>
                )}
              </button>
            </form>
          </div>

          {/* Footer note */}
          <p style={{ textAlign: 'center', fontFamily: FONT, fontSize: '12px', color: 'rgba(255,255,255,0.2)', marginTop: '28px' }}>
            🔒 Restricted access · Victory Homes Real Estate
          </p>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

/* ── Listing Detail Modal ──────────────────────────────────────────────────── */
function ListingModal({ listing, onClose, onApprove, onReject, onSold }) {
  const f = listing.form || {}
  const [imgIdx, setImgIdx] = useState(0)
  const [viewingDoc, setViewingDoc] = useState(null)
  const photos = listing.photos || []

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])

  const field = (label, val) => val ? (
    <div>
      <p style={{ fontFamily: FONT, fontSize: '10px', fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 3px' }}>{label}</p>
      <p style={{ fontFamily: FONT, fontSize: '14px', color: NAVY, margin: 0, fontWeight: 500 }}>{val}</p>
    </div>
  ) : null

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,18,36,0.75)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px', overflowY: 'auto' }}
    >
      <style>{`
        @keyframes modalIn { from { opacity:0; transform:translateY(24px) scale(0.97); } to { opacity:1; transform:none; } }
        .modal-scroll::-webkit-scrollbar { width: 5px; }
        .modal-scroll::-webkit-scrollbar-thumb { background: #dde2ea; border-radius: 4px; }
      `}</style>
      <div className="modal-scroll" style={{ background: '#fff', borderRadius: '20px', width: '100%', maxWidth: viewingDoc ? '1200px' : '720px', height: viewingDoc ? '90vh' : 'auto', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 40px 100px rgba(0,0,0,0.4)', animation: 'modalIn 0.28s ease', display: 'flex', flexDirection: 'column', transition: 'max-width 0.3s ease' }}>

        {viewingDoc ? (
          <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
              <h3 style={{ fontFamily: FONT, fontSize: '18px', color: NAVY, margin: 0, fontWeight: 700 }}>{viewingDoc.name || 'Document View'}</h3>
              <div style={{ display: 'flex', gap: '12px' }}>
                <a href={viewingDoc.data} download={viewingDoc.name || 'document'} style={{ padding: '8px 16px', borderRadius: '8px', background: NAVY, color: '#fff', border: 'none', cursor: 'pointer', fontFamily: FONT, fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                  Download
                </a>
                <button onClick={() => setViewingDoc(null)} style={{ padding: '8px 16px', borderRadius: '8px', background: `${NAVY}10`, color: NAVY, border: 'none', cursor: 'pointer', fontFamily: FONT, fontWeight: 600, transition: 'all 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = `${NAVY}20`} onMouseLeave={e => e.currentTarget.style.background = `${NAVY}10`}>← Back to Details</button>
              </div>
            </div>
            <div style={{ flex: 1, border: '1px solid #dde2ea', borderRadius: '12px', background: '#e2e8f0', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {viewingDoc.data.startsWith('data:image/') ? (
                <img src={viewingDoc.data} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="Document" />
              ) : (
                <iframe src={viewingDoc.data + '#toolbar=0&view=FitH'} style={{ width: '100%', height: '100%', border: 'none' }} title="Document Viewer" />
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Modal header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '22px 28px 18px', borderBottom: `3px solid ${GOLD}`, background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 100%)`, borderRadius: '20px 20px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <img src="/assets/Victory Homes logo design.png" alt="Victory Homes" style={{ height: '32px', filter: 'brightness(0) invert(1)', opacity: 0.9 }} />
            <div>
              <h2 style={{ fontFamily: FONT, fontSize: '17px', fontWeight: 700, color: '#fff', margin: '0 0 2px' }}>
                {f.propertyType || 'Property'} · {f.emirate || '—'}
              </h2>
              <p style={{ fontFamily: FONT, fontSize: '11px', color: 'rgba(255,255,255,0.45)', margin: 0 }}>
                Submitted {fmt(listing.submittedAt)}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <StatusBadge status={listing.status} />
            <button onClick={onClose} style={{ width: '34px', height: '34px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
            >×</button>
          </div>
        </div>

        <div style={{ padding: '24px 28px' }}>
          {/* Photo viewer */}
          {photos.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ borderRadius: '14px', overflow: 'hidden', height: '240px', background: '#f0f4f8' }}>
                <img src={photos[imgIdx]} alt="Property" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
              {photos.length > 1 && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px', overflowX: 'auto', paddingBottom: '4px' }}>
                  {photos.map((p, i) => (
                    <img key={i} src={p} alt="" onClick={() => setImgIdx(i)}
                      style={{ width: '68px', height: '52px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', flexShrink: 0, border: `2px solid ${i === imgIdx ? GOLD : 'transparent'}`, opacity: i === imgIdx ? 1 : 0.65, transition: 'all 0.15s' }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Price highlight */}
          <div style={{ background: `linear-gradient(120deg, ${NAVY}08 0%, ${GOLD}08 100%)`, border: `1px solid ${GOLD}25`, borderRadius: '14px', padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <p style={{ fontFamily: FONT, fontSize: '11px', fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 4px' }}>
                {listing.listing === 'rent' ? 'Annual Rent' : 'Asking Price'}
              </p>
              <p style={{ fontFamily: FONT, fontSize: '26px', fontWeight: 800, color: NAVY, margin: 0 }}>
                {f.currency || 'AED'} {Number(f.askingPrice || 0).toLocaleString()}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ background: NAVY, color: '#fff', fontFamily: FONT, fontSize: '11px', fontWeight: 700, padding: '5px 12px', borderRadius: '50px', textTransform: 'uppercase' }}>{listing.listing}</span>
              {f.completionStatus && <span style={{ background: GOLD, color: '#fff', fontFamily: FONT, fontSize: '11px', fontWeight: 700, padding: '5px 12px', borderRadius: '50px' }}>{f.completionStatus === 'ready' ? 'Ready' : 'Off-Plan'}</span>}
            </div>
          </div>

          {/* Fields grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px 20px', background: '#f8fafb', borderRadius: '14px', padding: '20px', marginBottom: '20px' }}>
            {field('Owner Name', f.ownerName)}
            {field('Email', f.email)}
            {field('Phone', f.phone)}
            {field('WhatsApp', f.whatsapp)}
            {field('Property Type', f.propertyType)}
            {field('Emirate', f.emirate)}
            {field('Community', f.community)}
            {field('Sub-Community', f.subCommunity)}
            {field('Bedrooms', f.bedrooms)}
            {field('Bathrooms', f.bathrooms)}
            {field('Area', f.size ? `${Number(f.size).toLocaleString()} sq ft` : null)}
            {field('Floor', f.floor)}
            {field('Furnishing', f.furnishing)}
            {field('Completion', f.completionStatus)}
            {f.youtubeLink && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontFamily: FONT, fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', marginBottom: '4px' }}>YouTube Link</span>
                <a href={f.youtubeLink} target="_blank" rel="noopener noreferrer" style={{ fontFamily: FONT, fontSize: '14px', color: '#3B82F6', fontWeight: 500, textDecoration: 'none' }}>Watch Video ↗</a>
              </div>
            )}
          </div>

          {f.description && (
            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontFamily: FONT, fontSize: '11px', fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 8px' }}>Description</p>
              <p style={{ fontFamily: FONT, fontSize: '14px', color: '#4a5568', lineHeight: '1.7', margin: 0, background: '#f8fafb', borderRadius: '10px', padding: '14px 16px' }}>{f.description}</p>
            </div>
          )}

          {/* Documents Section */}
          {listing.documents && (listing.documents.titleDeed?.length > 0 || listing.documents.floorPlan?.length > 0 || listing.documents.passport?.length > 0) && (
            <div style={{ marginBottom: '24px' }}>
              <p style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 700, color: NAVY, borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', margin: '0 0 16px' }}>Uploaded Documents</p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {['titleDeed', 'floorPlan', 'passport'].map(docKey => {
                  const docs = listing.documents[docKey];
                  if (!docs || docs.length === 0) return null;
                  const label = docKey === 'titleDeed' ? 'Title Deed' : docKey === 'floorPlan' ? 'Floor Plan' : 'Passport / ID';
                  return docs.map((doc, i) => (
                    <div key={`${docKey}-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafb', border: '1px solid #dde2ea', padding: '12px 16px', borderRadius: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: `${NAVY}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>📄</div>
                        <div>
                          <p style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: NAVY, margin: '0 0 2px' }}>{doc.name || `Document ${i + 1}`}</p>
                          <p style={{ fontFamily: FONT, fontSize: '11px', color: '#64748b', margin: 0 }}>{label}</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button type="button" onClick={() => setViewingDoc(doc)} style={{ padding: '6px 14px', borderRadius: '6px', background: `${NAVY}10`, border: '1px solid transparent', color: NAVY, fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
                           onMouseEnter={e => { e.currentTarget.style.background = `${NAVY}20` }}
                           onMouseLeave={e => { e.currentTarget.style.background = `${NAVY}10` }}>
                          View
                        </button>
                        <a href={doc.data} download={doc.name || `${docKey}-${i}`} style={{ padding: '6px 14px', borderRadius: '6px', background: '#fff', border: '1px solid #dde2ea', color: NAVY, fontFamily: FONT, fontSize: '12px', fontWeight: 600, textDecoration: 'none', transition: 'all 0.15s' }}
                           onMouseEnter={e => { e.currentTarget.style.borderColor = NAVY; e.currentTarget.style.color = NAVY }}
                           onMouseLeave={e => { e.currentTarget.style.borderColor = '#dde2ea'; e.currentTarget.style.color = NAVY }}>
                          Download
                        </a>
                      </div>
                    </div>
                  ));
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap', paddingTop: '8px', borderTop: '1px solid #f0f2f5' }}>
            {listing.status === 'pending' && (
              <>
                <button
                  onClick={() => { onReject(listing.id); onClose() }}
                  style={{ padding: '12px 28px', borderRadius: '10px', border: '1.5px solid #fca5a5', background: '#fff', color: '#dc2626', fontFamily: FONT, fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.06)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff' }}
                >
                  Reject Listing
                </button>
                <button
                  onClick={() => { onApprove(listing.id); onClose() }}
                  style={{ padding: '12px 28px', borderRadius: '10px', border: 'none', background: `linear-gradient(135deg, #059669 0%, #047857 100%)`, color: '#fff', fontFamily: FONT, fontSize: '14px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(5,150,105,0.3)', transition: 'all 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'none'}
                >
                  ✓ Approve & Publish
                </button>
              </>
            )}
            {listing.status === 'approved' && (
                <button
                  onClick={() => { onSold(listing.id); onClose() }}
                  style={{ padding: '12px 28px', borderRadius: '10px', border: 'none', background: NAVY, color: '#fff', fontFamily: FONT, fontSize: '14px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'none'}
                >
                  🏷️ Mark as Sold
                </button>
            )}
          </div>
          </div>
          </>
        )}
        </div>
      </div>
  )
}

/* ── Chat Logs Panel ──────────────────────────────────────────────────────── */
function ChatLogsPanel({ token }) {
  const [logs, setLogs]           = useState([])
  const [loading, setLoading]     = useState(true)
  const [expanded, setExpanded]   = useState(null)
  const [clearing, setClearing]   = useState(false)

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    try {
      const res  = await fetch('/api/chatbot/logs', { headers: { 'x-admin-token': token } })
      const data = await res.json()
      setLogs(Array.isArray(data) ? data : [])
    } catch {
      setLogs([])
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { fetchLogs() }, [fetchLogs])

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Delete this chat session?')) return
    await fetch(`/api/chatbot/logs/${sessionId}`, { method: 'DELETE', headers: { 'x-admin-token': token } })
    setLogs(prev => prev.filter(l => l.sessionId !== sessionId))
  }

  const clearAll = async () => {
    if (!window.confirm('Permanently delete ALL chat logs?')) return
    setClearing(true)
    await fetch('/api/chatbot/logs', { method: 'DELETE', headers: { 'x-admin-token': token } })
    setLogs([])
    setClearing(false)
  }

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
      <div style={{ width: '40px', height: '40px', border: `3px solid ${GOLD}30`, borderTopColor: GOLD, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  )

  return (
    <div style={{ padding: 'clamp(16px,3vw,28px)' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>AI Chatbot Conversations</h3>
          <p style={{ fontFamily: FONT, fontSize: '13px', color: '#64748b', margin: 0 }}>{logs.length} session{logs.length !== 1 ? 's' : ''} recorded</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={fetchLogs} style={{ padding: '9px 16px', borderRadius: '8px', border: `1.5px solid ${GOLD}40`, background: `${GOLD}08`, color: NAVY, fontFamily: FONT, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.background = `${GOLD}15` }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = `${GOLD}40`; e.currentTarget.style.background = `${GOLD}08` }}
          >↻ Refresh</button>
          {logs.length > 0 && (
            <button onClick={clearAll} disabled={clearing} style={{ padding: '9px 16px', borderRadius: '8px', border: '1.5px solid #fca5a5', background: '#fff', color: '#dc2626', fontFamily: FONT, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >{clearing ? 'Clearing…' : '🗑 Clear All'}</button>
          )}
        </div>
      </div>

      {logs.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: '16px', padding: '80px 20px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,46,83,0.06)' }}>
          <div style={{ fontSize: '52px', marginBottom: '16px' }}>💬</div>
          <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 8px' }}>No chat logs yet</h3>
          <p style={{ fontFamily: FONT, fontSize: '14px', color: '#94a3b8', margin: 0 }}>Conversations from the AI chatbot will appear here.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {logs.map((session) => (
            <div key={session.sessionId} style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 1px 6px rgba(0,46,83,0.06)', overflow: 'hidden', borderLeft: `3px solid ${GOLD}` }}>
              {/* Session row */}
              <div
                onClick={() => setExpanded(e => e === session.sessionId ? null : session.sessionId)}
                style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', flexWrap: 'wrap' }}
              >
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: `${NAVY}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>🤖</div>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <div style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 700, color: NAVY, marginBottom: '2px' }}>
                    {session.turns?.length || 0} message{(session.turns?.length || 0) !== 1 ? 's' : ''} &nbsp;·&nbsp;
                    <span style={{ fontWeight: 400, color: '#64748b' }}>{session.turns?.[0]?.pageUrl || '/'}</span>
                  </div>
                  <div style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8' }}>
                    Started {fmt(session.startedAt)} · Last activity {timeAgo(session.lastActivity)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => deleteSession(session.sessionId)} style={{ padding: '6px 12px', borderRadius: '7px', border: '1.5px solid #fca5a5', background: '#fff', color: '#dc2626', fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                  >Delete</button>
                  <span style={{ fontFamily: FONT, fontSize: '12px', color: '#94a3b8' }}>{expanded === session.sessionId ? '▲' : '▼'}</span>
                </div>
              </div>

              {/* Expanded transcript */}
              {expanded === session.sessionId && (
                <div style={{ borderTop: '1px solid #f0f4f8', padding: '16px 18px', background: '#fafbfc' }}>
                  {(session.turns || []).map((turn, i) => (
                    <div key={i} style={{ marginBottom: '16px' }}>
                      {/* User */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
                        <div style={{ maxWidth: '75%', background: `linear-gradient(135deg, ${GOLD}, #b8904a)`, color: '#fff', padding: '9px 14px', borderRadius: '12px 12px 3px 12px', fontFamily: FONT, fontSize: '13px', lineHeight: 1.5 }}>
                          {turn.userMsg}
                        </div>
                      </div>
                      {/* Bot */}
                      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <div style={{ maxWidth: '75%', background: '#fff', border: '1px solid #e8edf3', color: NAVY, padding: '9px 14px', borderRadius: '12px 12px 12px 3px', fontFamily: FONT, fontSize: '13px', lineHeight: 1.5, boxShadow: '0 1px 4px rgba(0,46,83,0.05)' }}>
                          {turn.botReply}
                          <div style={{ fontFamily: FONT, fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>{fmt(turn.timestamp)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Services Panel ──────────────────────────────────────────────────────── */
function ServicesPanel({ token, onStatsChange }) {
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchServices = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch('/api/admin/services', { headers: { 'x-admin-token': token } })
      const data = await res.json()
      setServices(Array.isArray(data) ? data.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt)) : [])
    } catch {
      setServices([])
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { fetchServices() }, [fetchServices])

  const archive = async (id) => {
    await fetch(`/api/admin/services/${id}/archive`, { method: 'PATCH', headers: { 'x-admin-token': token } })
    fetchServices()
    if (onStatsChange) onStatsChange()
  }

  const deleteRequest = async (id) => {
    if (!window.confirm('Delete this request permanently?')) return
    await fetch(`/api/admin/services/${id}`, { method: 'DELETE', headers: { 'x-admin-token': token } })
    fetchServices()
    if (onStatsChange) onStatsChange()
  }

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
      <div style={{ width: '40px', height: '40px', border: `3px solid ${GOLD}30`, borderTopColor: GOLD, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  )

  return (
    <div style={{ padding: 'clamp(16px,3vw,28px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>Service Requests</h3>
          <p style={{ fontFamily: FONT, fontSize: '13px', color: '#64748b', margin: 0 }}>{services.length} request{services.length !== 1 ? 's' : ''} total</p>
        </div>
        <button onClick={fetchServices} style={{ padding: '9px 16px', borderRadius: '8px', border: `1.5px solid ${GOLD}40`, background: `${GOLD}08`, color: NAVY, fontFamily: FONT, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.background = `${GOLD}15` }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = `${GOLD}40`; e.currentTarget.style.background = `${GOLD}08` }}
        >↻ Refresh</button>
      </div>

      {services.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: '16px', padding: '80px 20px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,46,83,0.06)' }}>
          <div style={{ fontSize: '52px', marginBottom: '16px' }}>🛎️</div>
          <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 8px' }}>No service requests</h3>
          <p style={{ fontFamily: FONT, fontSize: '14px', color: '#94a3b8', margin: 0 }}>Requests for Property Management, Holiday Homes, etc. will appear here.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '12px' }}>
          {services.map(s => {
            const f = s.form || {}
            return (
              <div key={s.id} style={{ background: '#fff', borderRadius: '14px', padding: '20px', borderLeft: `4px solid ${s.status === 'archived' ? '#94a3b8' : GOLD}`, boxShadow: '0 2px 8px rgba(0,46,83,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <h4 style={{ fontFamily: FONT, fontSize: '16px', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>{f.serviceRequested || 'Service'}</h4>
                    <span style={{ fontFamily: FONT, fontSize: '12px', color: '#64748b' }}>Submitted {new Date(s.submittedAt).toLocaleDateString()} {new Date(s.submittedAt).toLocaleTimeString()}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {s.status !== 'archived' && (
                      <button onClick={() => archive(s.id)} style={{ padding: '6px 12px', borderRadius: '6px', background: `${NAVY}10`, border: 'none', color: NAVY, fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s' }}>Mark as Read</button>
                    )}
                    <button onClick={() => deleteRequest(s.id)} style={{ padding: '6px 12px', borderRadius: '6px', background: 'transparent', border: '1px solid #fca5a5', color: '#dc2626', fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s' }}>Delete</button>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', background: '#f8fafb', padding: '12px', borderRadius: '8px' }}>
                  <div>
                    <div style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Client Name</div>
                    <div style={{ fontFamily: FONT, fontSize: '14px', color: NAVY, fontWeight: 500 }}>{f.fullName}</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Contact</div>
                    <div style={{ fontFamily: FONT, fontSize: '14px', color: NAVY, fontWeight: 500 }}>{f.email} <br/> {f.phone}</div>
                  </div>
                  {f.propertyDetails && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Property/Unit Details</div>
                      <div style={{ fontFamily: FONT, fontSize: '14px', color: '#4a5568' }}>{f.propertyDetails}</div>
                    </div>
                  )}
                  {f.message && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Message</div>
                      <div style={{ fontFamily: FONT, fontSize: '14px', color: '#4a5568' }}>{f.message}</div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* ── Dashboard ─────────────────────────────────────────────────────────────── */
function Dashboard({ token, onLogout }) {
  const [listings, setListings] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [activeTab, setActiveTab] = useState('listings') // 'listings' | 'chatlogs'
  const [selected, setSelected] = useState(null)
  const [toast, setToast] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [pendingNav, setPendingNav] = useState(null)
  const [stats, setStats] = useState({ services: 0, buyInquiries: 0, contactMessages: 0, brochure: 0 })
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3500)
  }

  const fetchListings = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const res = await fetch('/api/admin/listings', { headers: { 'x-admin-token': token } })
      if (res.status === 401) { onLogout(); return }
      const data = await res.json()
      setListings(data.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt)))
    } catch {
      if (!silent) showToast('Failed to fetch listings', 'error')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [token, onLogout])

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/dashboard-stats', { headers: { 'x-admin-token': token } })
      if (res.ok) {
        const data = await res.json()
        setStats({ services: data.servicesPending, buyInquiries: data.buyInquiriesPending, contactMessages: data.contactMessagesPending, brochure: data.brochurePending })
      }
    } catch { /* ignore */ }
  }, [token])

  useEffect(() => { fetchListings(); fetchStats() }, [fetchListings, fetchStats])

  const approve = async (id) => {
    try {
      const res = await fetch(`/api/admin/listings/${id}/approve`, { method: 'PATCH', headers: { 'x-admin-token': token } })
      if (!res.ok) throw new Error()
      setListings(prev => prev.map(l => l.id === id ? { ...l, status: 'approved', approvedAt: new Date().toISOString() } : l))
      showToast('Listing approved and published to the website!')
    } catch {
      showToast('Failed to approve listing', 'error')
    }
  }

  const reject = async (id) => {
    try {
      const res = await fetch(`/api/admin/listings/${id}/reject`, { method: 'PATCH', headers: { 'x-admin-token': token } })
      if (!res.ok) throw new Error()
      setListings(prev => prev.map(l => l.id === id ? { ...l, status: 'rejected', rejectedAt: new Date().toISOString() } : l))
      showToast('Listing rejected.')
    } catch {
      showToast('Failed to reject listing', 'error')
    }
  }

  const markSold = async (id) => {
    try {
      const res = await fetch(`/api/admin/listings/${id}/sold`, { method: 'PATCH', headers: { 'x-admin-token': token } })
      if (!res.ok) throw new Error()
      setListings(prev => prev.map(l => l.id === id ? { ...l, status: 'sold', soldAt: new Date().toISOString() } : l))
      showToast('Listing marked as sold.')
    } catch {
      showToast('Failed to mark listing as sold', 'error')
    }
  }

  const deleteListing = async () => {
    if (!deleteConfirm) return
    try {
      const res = await fetch(`/api/admin/listings/${deleteConfirm}`, { method: 'DELETE', headers: { 'x-admin-token': token } })
      if (!res.ok) throw new Error()
      setListings(prev => prev.filter(l => l.id !== deleteConfirm))
      setDeleteConfirm(null)
      showToast('Listing deleted.')
    } catch {
      showToast('Failed to delete listing', 'error')
    }
  }

  const filtered = filter === 'all' ? listings : listings.filter(l => l.status === filter)
  const counts = {
    all: listings.length,
    pending: listings.filter(l => l.status === 'pending').length,
    approved: listings.filter(l => l.status === 'approved').length,
    rejected: listings.filter(l => l.status === 'rejected').length,
  }

  // The sidebar navItems are replaced with hardcoded buttons to reduce crowding.

  const filterColors = {
    all:      { text: '#fff',    active: 'rgba(255,255,255,0.12)', dot: '#fff'    },
    pending:  { text: '#fbbf24', active: 'rgba(251,191,36,0.15)',  dot: '#f59e0b' },
    approved: { text: '#34d399', active: 'rgba(52,211,153,0.15)',  dot: '#10b981' },
    rejected: { text: '#f87171', active: 'rgba(248,113,113,0.15)', dot: '#ef4444' },
  }

  const sidebarWidth = sidebarCollapsed ? '72px' : '220px'

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f4f8', fontFamily: FONT }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: ${FONT}; }

        @media (max-width: 768px) {
          .adm-sidebar { transform: translateX(-100%) !important; }
          .adm-sidebar.open { transform: translateX(0) !important; width: 220px !important; }
          .adm-main { margin-left: 0 !important; }
          .adm-hamburger { display: flex !important; }
          .adm-stats { grid-template-columns: repeat(2, 1fr) !important; }
          .adm-sidebar-label { opacity: 1 !important; max-width: 200px !important; }
          .adm-sidebar-count { opacity: 1 !important; max-width: 40px !important; }
          .adm-sidebar-section-label { opacity: 1 !important; max-height: 24px !important; margin-bottom: 10px !important; }
          .adm-sidebar-logo-full { display: block !important; }
          .adm-sidebar-logo-icon { display: none !important; }
          .adm-collapse-btn { display: none !important; }
        }
        @media (min-width: 769px) {
          .adm-hamburger { display: none !important; }
        }
        @media (max-width: 520px) {
          .adm-stats { grid-template-columns: repeat(2, 1fr) !important; }
          .adm-topbar-title { font-size: 14px !important; }
        }
        @media (max-width: 360px) {
          .adm-stats { grid-template-columns: 1fr !important; }
        }

        @keyframes toastIn  { from { opacity:0; transform:translateY(16px) scale(0.96); } to { opacity:1; transform:none; } }
        @keyframes spin     { to   { transform: rotate(360deg); } }
        @keyframes fadeIn   { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:none; } }
        @keyframes sidebarSlide { from { opacity:0; } to { opacity:1; } }

        .adm-card { transition: transform 0.2s, box-shadow 0.2s; }
        .adm-card:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(0,46,83,0.13) !important; }
        .adm-row-hover { transition: background 0.15s; }
        .adm-row-hover:hover { background: #f7f9fc !important; }

        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-thumb { background: #c8d4e0; border-radius: 4px; }
        
        .adm-sidebar-nav::-webkit-scrollbar { display: none; }
        .adm-sidebar-nav { -ms-overflow-style: none; scrollbar-width: none; }

        .adm-sidebar-label {
          opacity: 1;
          max-width: 200px;
          overflow: hidden;
          white-space: nowrap;
          transition: opacity 0.2s, max-width 0.2s;
        }
        .adm-sidebar-label.collapsed {
          opacity: 0;
          max-width: 0;
          pointer-events: none;
        }
        .adm-sidebar-count {
          opacity: 1;
          max-width: 40px;
          transition: opacity 0.2s, max-width 0.2s;
        }
        .adm-sidebar-count.collapsed {
          opacity: 0;
          max-width: 0;
          pointer-events: none;
        }
        .adm-sidebar-section-label {
          opacity: 1;
          max-height: 24px;
          margin-bottom: 10px;
          overflow: hidden;
          transition: opacity 0.2s, max-height 0.2s, margin 0.2s;
        }
        .adm-sidebar-section-label.collapsed {
          opacity: 0;
          max-height: 0;
          margin-bottom: 0;
        }
        .adm-sidebar-logo-full {
          display: block;
          transition: opacity 0.2s;
        }
        .adm-sidebar-logo-full.collapsed {
          display: none;
        }
        .adm-sidebar-logo-icon {
          display: none;
        }
        .adm-sidebar-logo-icon.collapsed {
          display: block;
        }
        .adm-collapse-btn {
          transition: transform 0.3s;
        }
        .adm-collapse-btn.collapsed {
          transform: rotate(180deg);
        }
      `}</style>

      {/* Sidebar overlay (mobile) */}
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 998, backdropFilter: 'blur(2px)' }} />}

      {/* ── Sidebar ── */}
      <aside className={`adm-sidebar${sidebarOpen ? ' open' : ''}`} style={{
        position: 'fixed', top: 0, left: 0, bottom: 0, width: sidebarWidth,
        background: '#ffffff',
        borderRight: '1px solid #eaeef3',
        display: 'flex', flexDirection: 'column', zIndex: 999,
        transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1), transform 0.3s cubic-bezier(0.4,0,0.2,1)',
      }}>
        {/* Logo in sidebar */}
        <div style={{ padding: '0', minHeight: '64px', borderBottom: '1px solid #eaeef3', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', flexShrink: 0, overflow: 'visible' }}>
          <img
            src="/assets/Victory Homes logo design.png"
            alt="Victory Homes"
            style={{ width: sidebarCollapsed ? '48px' : '130px', height: 'auto', objectFit: 'contain', display: 'block', transition: 'all 0.3s' }}
          />
          <button
            className={`adm-collapse-btn${sidebarCollapsed ? ' collapsed' : ''}`}
            onClick={() => setSidebarCollapsed(c => !c)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{ position: 'absolute', right: '-12px', top: '50%', transform: 'translateY(-50%)', width: '24px', height: '24px', borderRadius: '50%', border: '1px solid #eaeef3', background: '#ffffff', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', zIndex: 1000, boxShadow: '0 2px 4px rgba(0,0,0,0.05)', transition: 'all 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f0f4f8'; e.currentTarget.style.color = '#0b2347' }}
            onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.color = '#94a3b8' }}
          >{sidebarCollapsed ? '›' : '‹'}</button>
        </div>

        {/* Navigation */}
        <nav className="adm-sidebar-nav" style={{ padding: sidebarCollapsed ? '24px 8px' : '24px 10px', flex: 1, overflowY: 'auto' }}>
          
          {(() => {
            const NavItem = ({ tab, icon, label, count, alertCount }) => {
              const isActive = activeTab === tab;
              return (
                <button
                  className="adm-nav-btn"
                  onClick={() => {
                    if (window.__adminUnsavedChanges) { setPendingNav({ tab }); return; }
                    setActiveTab(tab); setSidebarOpen(false);
                  }}
                  title={sidebarCollapsed ? label : undefined}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    padding: sidebarCollapsed ? '10px 0' : '8px 10px',
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    borderRadius: '6px', border: 'none', cursor: 'pointer',
                    background: isActive ? '#0b2347' : 'transparent',
                    marginBottom: '4px', textAlign: 'left', gap: sidebarCollapsed ? '0' : '10px',
                    color: isActive ? '#ffffff' : '#475569',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f8fafb' }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: isActive ? '#ffffff' : '#64748b' }}>
                    {icon}
                  </span>
                  {!sidebarCollapsed && <span className="adm-sidebar-label" style={{ flex: 1, fontFamily: FONT, fontSize: '13px', fontWeight: isActive ? 600 : 500 }}>{label}</span>}
                  {count > 0 && !sidebarCollapsed && (
                    <span className="adm-sidebar-count" style={{ background: isActive ? 'rgba(255,255,255,0.15)' : (alertCount ? '#ef4444' : '#e2e8f0'), color: isActive ? '#fff' : (alertCount ? '#fff' : '#475569'), fontFamily: FONT, fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '50px', minWidth: '20px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {count}
                    </span>
                  )}
                  {count > 0 && sidebarCollapsed && (
                    <span style={{ position: 'absolute', right: '4px', top: '4px', width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444' }} />
                  )}
                </button>
              )
            }

            const icons = {
              home: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>,
              inbox: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"></polyline><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path></svg>,
              msg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>,
              tag: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>,
              mail: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>,
              build: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></svg>,
              file: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>,
              users: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
              shield: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>,
              ext: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
            }

            return (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Core Properties */}
                <NavItem tab="listings" icon={icons.home} label="Resale Properties" count={counts.pending} alertCount={true} />
                <NavItem tab="offplan" icon={icons.build} label="Properties Portfolio" />
                
                {/* Leads & Messages */}
                <NavItem tab="buyinquiries" icon={icons.tag} label="Sales Inquiries" count={stats.buyInquiries} alertCount={true} />
                <NavItem tab="brochure" icon={icons.file} label="Brochure Downloads" count={stats.brochure} alertCount={true} />
                <NavItem tab="services" icon={icons.inbox} label="Service Requests" count={stats.services} alertCount={true} />
                <NavItem tab="contactmsgs" icon={icons.mail} label="General Inquiries" count={stats.contactMessages} alertCount={true} />
                
                {/* System & Management */}
                <NavItem tab="chatlogs" icon={icons.msg} label="AI Chat History" />
                <NavItem tab="team" icon={icons.users} label="Agents & Team" />
                <NavItem tab="trust" icon={icons.shield} label="Testimonials & Trust" />
                <NavItem tab="settings" icon={icons.build} label="Currency Settings" />
                <div style={{ margin: '8px 4px', height: '1px', background: '#eaeef3' }} />
                <a href="/"
                  title={sidebarCollapsed ? 'View Website' : undefined}
                  style={{ display: 'flex', alignItems: 'center', gap: sidebarCollapsed ? '0' : '10px', padding: sidebarCollapsed ? '10px 0' : '8px 10px', justifyContent: sidebarCollapsed ? 'center' : 'flex-start', borderRadius: '6px', textDecoration: 'none', color: '#475569', fontFamily: FONT, fontSize: '13px', fontWeight: 500, transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f8fafb' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>{icons.ext}</span>
                  {!sidebarCollapsed && <span className="adm-sidebar-label">View Website</span>}
                </a>
              </div>
            )
          })()}
        </nav>

        {/* User / Logout */}
        <div style={{ borderTop: '1px solid #eaeef3', background: '#f8fafb' }}>
          <div style={{ padding: sidebarCollapsed ? '12px 8px' : '12px 14px', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#0b2347', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT, fontSize: '13px', fontWeight: 700, flexShrink: 0 }}>VH</div>
            {!sidebarCollapsed && (
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 700, color: '#0b2347', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>Super Admin</div>
                <div style={{ fontFamily: FONT, fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '2px' }}>admin@victory.com</div>
              </div>
            )}
          </div>
          <div style={{ padding: sidebarCollapsed ? '0 8px 12px' : '0 14px 12px' }}>
            <button
              onClick={onLogout}
              title={sidebarCollapsed ? 'Sign Out' : undefined}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: sidebarCollapsed ? '0' : '8px', padding: sidebarCollapsed ? '8px 0' : '6px 10px', justifyContent: 'center', borderRadius: '6px', border: 'none', background: 'transparent', color: '#ef4444', fontFamily: FONT, fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
              </span>
              {!sidebarCollapsed && <span className="adm-sidebar-label">Logout</span>}
            </button>
          </div>
        </div>
      </aside>
{/* ── Main ── */}
      <main className="adm-main" style={{ flex: 1, marginLeft: sidebarWidth, minWidth: 0, display: 'flex', flexDirection: 'column', transition: 'margin-left 0.3s cubic-bezier(0.4,0,0.2,1)' }}>

        {/* Top bar */}
        <div style={{ background: '#fff', borderBottom: '1px solid #eaeef3', padding: '16px clamp(16px,3vw,32px)', minHeight: '88px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 12px rgba(0,46,83,0.07)', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button className="adm-hamburger" onClick={() => setSidebarOpen(o => !o)} style={{ display: 'none', width: '36px', height: '36px', borderRadius: '8px', border: '1.5px solid #eaeef3', background: '#f8fafb', cursor: 'pointer', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }} aria-label="Menu">☰</button>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <h2 className="adm-topbar-title" style={{ fontFamily: FONT, fontSize: 'clamp(18px,2.5vw,22px)', fontWeight: 700, color: NAVY, lineHeight: 1.2, margin: 0 }}>
                {activeTab === 'chatlogs' ? '💬 AI Chat History' : activeTab === 'services' ? '🛎️ Service Requests' : activeTab === 'offplan' ? '🏗️ Properties Portfolio' : activeTab === 'brochure' ? '📄 Brochure Downloads' : activeTab === 'team' ? '👥 Agents & Team' : activeTab === 'trust' ? '⭐ Testimonials & Trust' : activeTab === 'settings' ? '💱 Currency Settings' : activeTab === 'buyinquiries' ? '💎 Sales Inquiries' : activeTab === 'contactmsgs' ? '✉️ General Inquiries' : '🏠 Resale Properties'}
              </h2>
              <p style={{ fontFamily: FONT, fontSize: '13px', color: '#64748b', margin: '4px 0 0', fontWeight: 500 }}>
                {activeTab === 'chatlogs' ? 'AI chatbot conversations' : activeTab === 'services' ? 'Manage property service inquiries' : activeTab === 'offplan' ? 'Manage off-plan, buy, and rent properties' : activeTab === 'brochure' ? 'View brochure downloads' : activeTab === 'team' ? 'Manage company agents and staff' : activeTab === 'settings' ? 'Manage exchange rates and global configurations' : activeTab === 'buyinquiries' ? 'Review property purchase inquiries' : activeTab === 'contactmsgs' ? 'View general contact messages' : `${filtered.length} ${filtered.length === 1 ? 'entry' : 'entries'}`}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {activeTab === 'listings' && (
              <button onClick={fetchListings} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 18px', borderRadius: '8px', border: `1.5px solid ${GOLD}40`, background: `${GOLD}08`, color: NAVY, fontFamily: FONT, fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.background = `${GOLD}15` }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = `${GOLD}40`; e.currentTarget.style.background = `${GOLD}08` }}
              >↻ Refresh</button>
            )}
          </div>
        </div>

        {/* Services Panel */}
        {activeTab === 'services' && <ServicesPanel token={token} onStatsChange={fetchStats} />}

        {/* Chat Logs Panel */}
        {activeTab === 'chatlogs' && <ChatLogsPanel token={token} />}

        {/* Offplan Panel */}
        {activeTab === 'offplan' && <AdminOffplanPanel token={token} />}

        {/* Brochure Panel */}
        {activeTab === 'brochure' && <AdminBrochurePanel token={token} onStatsChange={fetchStats} />}

        {/* Team Panel */}
        {activeTab === 'team' && <AdminTeamPanel token={token} />}

        {/* Buy Inquiries Panel */}
        {activeTab === 'buyinquiries' && <AdminBuyPanel token={token} onStatsChange={fetchStats} />}

        {/* Contact Messages Panel */}
        {activeTab === 'contactmsgs' && <AdminContactPanel token={token} onStatsChange={fetchStats} />}

        {/* Trust Elements Panel */}
        {activeTab === 'trust' && <AdminTrustPanel token={token} />}

        {/* Settings Panel */}
        {activeTab === 'settings' && <AdminSettingsPanel token={token} />}

        {/* Listings view */}
        {activeTab === 'listings' && <>

        {/* Stats */}
        <div className="adm-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', padding: 'clamp(16px,3vw,28px) clamp(16px,3vw,28px) 0' }}>
          {[
            { label: 'Total',    value: counts.all,      border: NAVY,     icon: '📋', sub: 'all submissions'    },
            { label: 'Pending',  value: counts.pending,  border: '#d97706', icon: '⏳', sub: 'awaiting review'    },
            { label: 'Approved', value: counts.approved, border: '#059669', icon: '✓',  sub: 'live on website'    },
            { label: 'Rejected', value: counts.rejected, border: '#dc2626', icon: '✕',  sub: 'not published'      },
          ].map(({ label, value, border, icon, sub }) => (
            <div key={label} className="adm-card" style={{ background: '#fff', borderRadius: '16px', padding: '20px 22px', boxShadow: '0 2px 12px rgba(0,46,83,0.06)', borderTop: `3px solid ${border}`, animation: 'fadeIn 0.4s ease' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: `${border}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: 700, color: border }}>{icon}</div>
              </div>
              <div style={{ fontFamily: FONT, fontSize: '30px', fontWeight: 800, color: NAVY, lineHeight: 1, marginBottom: '4px' }}>{value}</div>
              <div style={{ fontFamily: FONT, fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
              <div style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{sub}</div>
            </div>
          ))}
        </div>

        {/* Filter Tabs */}
        <div style={{ padding: '20px clamp(16px,3vw,28px) 0' }}>
          <div style={{ display: 'flex', gap: '8px', background: '#fff', padding: '6px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,46,83,0.06)', width: 'fit-content', flexWrap: 'wrap' }}>
            {['all', 'pending', 'approved', 'sold', 'rejected'].map(k => (
              <button key={k} onClick={() => setFilter(k)} style={{ padding: '8px 16px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT, fontSize: '13px', fontWeight: filter === k ? 700 : 500, color: filter === k ? '#fff' : '#64748b', background: filter === k ? NAVY : 'transparent', transition: 'all 0.15s', textTransform: 'capitalize' }}>
                {k} {k !== 'all' && `(${listings.filter(l => l.status === k).length})`}
              </button>
            ))}
          </div>
        </div>

        {/* Listings list */}
        <div style={{ padding: 'clamp(16px,3vw,28px)' }}>
          {loading ? (
            <div style={{ background: '#fff', borderRadius: '16px', padding: '80px 20px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,46,83,0.06)' }}>
              <div style={{ width: '40px', height: '40px', border: `3px solid ${GOLD}30`, borderTopColor: GOLD, borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 16px' }} />
              <p style={{ fontFamily: FONT, fontSize: '14px', color: '#94a3b8' }}>Loading submissions…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: '16px', padding: '80px 20px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,46,83,0.06)', animation: 'fadeIn 0.3s ease' }}>
              <div style={{ fontSize: '52px', marginBottom: '16px' }}>📭</div>
              <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 8px' }}>
                {filter === 'all' ? 'No submissions yet' : `No ${filter} listings`}
              </h3>
              <p style={{ fontFamily: FONT, fontSize: '14px', color: '#94a3b8', margin: 0 }}>
                Listings submitted from the Sell/Rent page will appear here.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filtered.map((listing, idx) => {
                const f = listing.form || {}
                return (
                  <div
                    key={listing.id}
                    className="adm-row-hover"
                    onClick={() => setSelected(listing)}
                    style={{ background: '#fff', borderRadius: '14px', padding: '16px 20px', boxShadow: '0 1px 6px rgba(0,46,83,0.06)', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', cursor: 'pointer', animation: `fadeIn 0.3s ease ${idx * 0.04}s both`, borderLeft: `3px solid ${listing.status === 'approved' ? '#10b981' : listing.status === 'rejected' ? '#ef4444' : GOLD}` }}
                  >
                    {/* Thumbnail */}
                    <div style={{ width: '68px', height: '52px', borderRadius: '10px', overflow: 'hidden', background: '#f0f4f8', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {listing.photos?.[0] ? (
                        <img src={listing.photos[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: '22px' }}>🏠</span>
                      )}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: '140px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 700, color: NAVY }}>{f.propertyType || 'Property'}{f.emirate ? ` · ${f.emirate}` : ''}</span>
                        <span style={{ fontFamily: FONT, fontSize: '10px', fontWeight: 700, color: GOLD, background: `${GOLD}15`, padding: '2px 8px', borderRadius: '50px', textTransform: 'uppercase', border: `1px solid ${GOLD}30` }}>{listing.listing}</span>
                        <StatusBadge status={listing.status} />
                      </div>
                      <p style={{ fontFamily: FONT, fontSize: '12px', color: '#94a3b8', margin: 0 }}>
                        {f.ownerName || '—'} · {timeAgo(listing.submittedAt)}
                        {f.community ? ` · ${f.community}` : ''}
                        {f.bedrooms ? ` · ${f.bedrooms} bed` : ''}
                      </p>
                    </div>

                    {/* Price */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 800, color: NAVY }}>{f.currency || 'AED'} {Number(f.askingPrice || 0).toLocaleString()}</div>
                      {f.size && <div style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8' }}>{Number(f.size).toLocaleString()} sqft</div>}
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                      {listing.status === 'pending' && (
                        <>
                          <button onClick={() => reject(listing.id)} style={{ padding: '7px 14px', borderRadius: '8px', border: '1.5px solid #fca5a5', background: '#fff', color: '#dc2626', fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                          >Reject</button>
                          <button onClick={() => approve(listing.id)} style={{ padding: '7px 16px', borderRadius: '8px', border: 'none', background: '#059669', color: '#fff', fontFamily: FONT, fontSize: '12px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(5,150,105,0.3)', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#047857'}
                            onMouseLeave={e => e.currentTarget.style.background = '#059669'}
                          >Approve</button>
                        </>
                      )}
                      <button onClick={(e) => deleteListing(listing.id, e)} style={{ width: '34px', height: '34px', borderRadius: '8px', border: '1.5px solid #eaeef3', background: '#f8fafb', color: '#94a3b8', fontFamily: FONT, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.color = '#dc2626' }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#f8fafb'; e.currentTarget.style.borderColor = '#eaeef3'; e.currentTarget.style.color = '#94a3b8' }}
                        title="Delete"
                      >🗑</button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        </> }
      </main>

      {/* Unsaved Changes Modal Overlay */}
      {pendingNav && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,16,32,0.6)', backdropFilter: 'blur(8px)' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '90%', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', border: `1px solid ${GOLD}40`, animation: 'cardIn 0.2s ease' }}>
            <div style={{ fontSize: '36px', marginBottom: '16px', textAlign: 'center' }}>⚠️</div>
            <h3 style={{ fontFamily: FONT, fontSize: '20px', fontWeight: 700, color: NAVY, textAlign: 'center', margin: '0 0 12px' }}>Unsaved Changes</h3>
            <p style={{ fontFamily: FONT, fontSize: '14px', color: '#64748b', textAlign: 'center', margin: '0 0 24px', lineHeight: 1.6 }}>You are currently editing an off-plan project. If you navigate away now, all your unsaved work will be lost. Are you sure you want to leave?</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setPendingNav(null)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #d1d5db', background: '#fff', color: NAVY, fontFamily: FONT, fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafb'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>Keep Editing</button>
              <button onClick={() => {
                  window.__adminUnsavedChanges = false;
                  if (pendingNav.tab) setActiveTab(pendingNav.tab);
                  if (pendingNav.filterKey) setFilter(pendingNav.filterKey);
                  setSidebarOpen(false);
                  setPendingNav(null);
              }} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#dc2626', color: '#fff', fontFamily: FONT, fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(220,38,38,0.2)', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#b91c1c'} onMouseLeave={e => e.currentTarget.style.background = '#dc2626'}>Discard & Leave</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {selected && (
        <ListingModal
          listing={selected}
          onClose={() => setSelected(null)}
          onApprove={(id) => { approve(id); setSelected(null) }}
          onReject={(id) => { reject(id); setSelected(null) }}
          onSold={(id) => { markSold(id); setSelected(null) }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: '28px', right: '28px', zIndex: 99999, background: toast.type === 'error' ? '#dc2626' : NAVY, color: '#fff', fontFamily: FONT, fontSize: '13px', fontWeight: 600, padding: '14px 20px', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', animation: 'toastIn 0.3s ease', display: 'flex', alignItems: 'center', gap: '10px', maxWidth: '340px', borderLeft: `4px solid ${toast.type === 'error' ? '#f87171' : GOLD}` }}>
          <span style={{ fontSize: '16px' }}>{toast.type === 'error' ? '⚠️' : '✓'}</span>
          {toast.msg}
        </div>
      )}
    </div>
  )
}

/* ── Main Export ───────────────────────────────────────────────────────────── */
export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY) || null)

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY)
    setToken(null)
  }

  return token
    ? <Dashboard token={token} onLogout={handleLogout} />
    : <LoginPage onLogin={setToken} />
}
